// AdmtReportsPage.js - ADMT archive search page (/admt)
// Mirrors ReportsPage.js exactly (same Sidebar/TableComponent/Pagination),
// but reads from the ADMT_archive.admt_reportsearch_flat table via
// admtApiService instead of the main report table.
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Search,
  FileText,
  ChevronRight,
  Menu,
  X,
  ExternalLink,
  Trash2,
  RefreshCw,
  AlertCircle,
  Info,
} from "lucide-react";
import { apiService, admtApiService } from "../services/api";
import LoadingScreen from "../components/LoadingScreen";
import Sidebar from "../components/Sidebar";
import TableComponent from "../components/TableComponent";
import Pagination from "../components/Pagination";
import { useToast } from "../contexts/ToastContext";
import useAdmtTableConfiguration from "../hooks/useAdmtTableConfiguration";

const AdmtReportsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const searchTimeoutRef = useRef(null);
  const initializingRef = useRef(false);

  const previousFiltersRef = useRef(null);
  const previousGeneralSearchRef = useRef(null);
  const countTimeoutRef = useRef(null);
  const countCallInProgressRef = useRef(false);

  const { getConfigurationForReportType, getTableHeaders } =
    useAdmtTableConfiguration();

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [generalSearch, setGeneralSearch] = useState("");
  const [activeTab, setActiveTab] = useState("RTC");
  const [tabCounts, setTabCounts] = useState({});

  const [showSearchInfo, setShowSearchInfo] = useState(false);
  const searchInfoRef = useRef(null);

  const [bookmarkedIds, setBookmarkedIds] = useState([]);
  const [pdfReportId, setPdfReportId] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  const [filters, setFilters] = useState({
    report_type: "",
    sales_area: "",
    entity: "",
    customer_name: "",
    customer_entity: [],
    country_name: "",
    site_name: "",
    date_from: "",
    date_to: "",
    failure_reason: "",
    part_number: "",
    description: "",
    rig_model: "",
    rig_id: "",
  });

  // ADMT archive only contains these three report types
  const tabs = [
    { key: "RTC", label: "Rock tool cards", reportType: "RTC" },
    { key: "CVR", label: "Customer visit reports", reportType: "CVR" },
    { key: "SA", label: "Site audits", reportType: "SA" },
  ];

  const createFilterSignature = useCallback((filters, generalSearch) => {
    const filtersCopy = { ...filters };
    if (!filtersCopy.date_from || !filtersCopy.date_to) {
      delete filtersCopy.date_from;
      delete filtersCopy.date_to;
    }
    delete filtersCopy.report_type;
    return JSON.stringify({
      filters: filtersCopy,
      generalSearch: generalSearch?.trim() || "",
    });
  }, []);

  const fetchTabCounts = useCallback(
    async (currentFilters, generalSearchTerm = "", forceRefresh = false) => {
      try {
        const currentSignature = createFilterSignature(currentFilters, generalSearchTerm);

        const shouldUpdate =
          forceRefresh ||
          !previousFiltersRef.current ||
          !previousGeneralSearchRef.current ||
          previousFiltersRef.current !== currentSignature;

        if (!shouldUpdate) return;
        if (countCallInProgressRef.current && !forceRefresh) return;

        if (countTimeoutRef.current) clearTimeout(countTimeoutRef.current);
        countCallInProgressRef.current = true;

        countTimeoutRef.current = setTimeout(async () => {
          const baseFilters = { ...currentFilters };
          delete baseFilters.report_type;
          if (!baseFilters.date_from || !baseFilters.date_to) {
            delete baseFilters.date_from;
            delete baseFilters.date_to;
          }

          const trimmedTerm = generalSearchTerm ? generalSearchTerm.trim() : "";
          const isWildcardSearch = trimmedTerm.startsWith("*");
          const hasGeneralSearch = trimmedTerm !== "" && !isWildcardSearch;

          try {
            let counts = {};

            if (isWildcardSearch) {
              const partNumberTerm = trimmedTerm.slice(1).trim();
              const countPromises = tabs.map(async (tab) => {
                try {
                  const tabFilters = {
                    ...baseFilters,
                    report_type: tab.reportType,
                    ...(partNumberTerm ? { part_number: partNumberTerm } : {}),
                  };
                  const result = await admtApiService.getFilteredCount(tabFilters);
                  return { key: tab.key, count: result.count };
                } catch (error) {
                  console.error(`Error fetching ADMT count for tab ${tab.key}:`, error);
                  return { key: tab.key, count: 0 };
                }
              });
              const results = await Promise.all(countPromises);
              counts = results.reduce((acc, { key, count }) => ({ ...acc, [key]: count }), {});
            } else if (hasGeneralSearch) {
              const countPromises = tabs.map(async (tab) => {
                try {
                  const tabFilters = { ...baseFilters, report_type: tab.reportType };
                  const result = await admtApiService.getCombinedSearchCount(tabFilters, trimmedTerm);
                  return { key: tab.key, count: result.count };
                } catch (error) {
                  console.error(`Error fetching ADMT count for tab ${tab.key}:`, error);
                  return { key: tab.key, count: 0 };
                }
              });
              const results = await Promise.all(countPromises);
              counts = results.reduce((acc, { key, count }) => ({ ...acc, [key]: count }), {});
            } else {
              const countPromises = tabs.map(async (tab) => {
                try {
                  const tabFilters = { ...baseFilters, report_type: tab.reportType };
                  const result = await admtApiService.getFilteredCount(tabFilters);
                  return { key: tab.key, count: result.count };
                } catch (error) {
                  console.error(`Error fetching ADMT count for tab ${tab.key}:`, error);
                  return { key: tab.key, count: 0 };
                }
              });
              const results = await Promise.all(countPromises);
              counts = results.reduce((acc, { key, count }) => ({ ...acc, [key]: count }), {});
            }

            setTabCounts(counts);
            previousFiltersRef.current = currentSignature;
            previousGeneralSearchRef.current = currentSignature;
          } catch (error) {
            console.error("Error fetching ADMT tab counts:", error);
            setTabCounts({});
          } finally {
            countCallInProgressRef.current = false;
          }
        }, 500);
      } catch (error) {
        console.error("Error in fetchTabCounts:", error);
        setTabCounts({});
        countCallInProgressRef.current = false;
      }
    },
    [tabs, createFilterSignature]
  );

  useEffect(() => {
    if (!initializingRef.current) {
      initializePage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializePage = () => {
    initializingRef.current = true;

    const urlPage = parseInt(searchParams.get("page")) || 1;
    const urlPageSize = parseInt(searchParams.get("pageSize")) || 20;
    const urlTab = searchParams.get("tab") || "RTC";
    const urlSearch = searchParams.get("search") || "";

    const urlFilters = { ...filters };
    searchParams.forEach((value, key) => {
      if (key !== "page" && key !== "pageSize" && key !== "tab" && key !== "search") {
        if (key === "customer_entity") {
          try {
            urlFilters[key] = JSON.parse(value);
          } catch {
            urlFilters[key] = [];
          }
        } else {
          urlFilters[key] = value;
        }
      }
    });

    setFilters(urlFilters);
    setCurrentPage(urlPage);
    setPageSize(urlPageSize);
    setActiveTab(urlTab);
    setGeneralSearch(urlSearch);

    const initTimeout = setTimeout(() => {
      initializingRef.current = false;
      fetchData(urlPage, false, urlTab, urlFilters, urlSearch, urlPageSize, true);
      fetchTabCounts(urlFilters, urlSearch, true);
    }, 100);

    return () => {
      clearTimeout(initTimeout);
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (countTimeoutRef.current) clearTimeout(countTimeoutRef.current);
    };
  };

  const updateURL = useCallback(
    (newFilters, newPage, newPageSize, newTab, newGeneralSearch) => {
      if (initializingRef.current) return;

      const params = new URLSearchParams();
      Object.entries(newFilters).forEach(([key, value]) => {
        if (key === "customer_entity") {
          if (Array.isArray(value) && value.length > 0) {
            params.set(key, JSON.stringify(value));
          }
        } else if (value && typeof value === "string" && value.trim() !== "") {
          params.set(key, value);
        }
      });

      if (newPage > 1) params.set("page", newPage.toString());
      if (newPageSize !== 20) params.set("pageSize", newPageSize.toString());
      if (newTab !== "RTC") params.set("tab", newTab);
      if (newGeneralSearch && newGeneralSearch.trim() !== "") params.set("search", newGeneralSearch.trim());

      setSearchParams(params, { replace: true });
    },
    [setSearchParams]
  );

  const fetchData = useCallback(
    async (
      page = 1,
      resetPage = false,
      specificTab = null,
      specificFilters = null,
      specificSearch = null,
      specificPageSize = null,
      skipCounts = false
    ) => {
      if (loading) return;

      setLoading(true);
      try {
        const actualPage = resetPage ? 1 : page;
        const currentTabKey = specificTab || activeTab;
        const currentFilters = specificFilters || filters;
        const currentSearch = specificSearch !== null ? specificSearch : generalSearch;
        const currentPageSize = specificPageSize || pageSize;

        const searchFilters = { ...currentFilters };

        if (searchFilters.date_from && searchFilters.date_to) {
          // both present - keep
        } else if (searchFilters.date_from || searchFilters.date_to) {
          delete searchFilters.date_from;
          delete searchFilters.date_to;
        }

        const currentTab = tabs.find((t) => t.key === currentTabKey);
        if (currentTab) {
          searchFilters.report_type = currentTab.reportType;
        }

        const trimmedSearch = currentSearch ? currentSearch.trim() : "";
        const isWildcardSearch = trimmedSearch.startsWith("*");
        const hasGeneralSearch = trimmedSearch !== "" && !isWildcardSearch;

        let result;

        if (isWildcardSearch) {
          const partNumberTerm = trimmedSearch.slice(1).trim();
          const wildcardFilters = { ...searchFilters };
          if (partNumberTerm) wildcardFilters.part_number = partNumberTerm;
          result = await admtApiService.searchDocuments(wildcardFilters, actualPage, currentPageSize);
        } else if (hasGeneralSearch) {
          result = await admtApiService.combinedSearch(searchFilters, trimmedSearch, actualPage, currentPageSize);
        } else {
          result = await admtApiService.searchDocuments(searchFilters, actualPage, currentPageSize);
        }

        const reportIds = (result.data || []).map((item) => item.report_id).filter(Boolean);
        if (reportIds.length > 0) {
          apiService.bulkCheckFavorites(reportIds).then((favoritedIds) => {
            setBookmarkedIds(favoritedIds);
          });
        } else {
          setBookmarkedIds([]);
        }

        setData(result.data);
        setTotalCount(result.total_count);
        setTotalPages(Math.ceil(result.total_count / currentPageSize));
        setCurrentPage(actualPage);

        updateURL(currentFilters, actualPage, currentPageSize, currentTabKey, currentSearch);

        if (!skipCounts && !initializingRef.current) {
          fetchTabCounts(currentFilters, currentSearch, false);
          showToast(`Found ${result.total_count} results`, "success");
        }
      } catch (error) {
        console.error("Error fetching ADMT data:", error);
        if (!initializingRef.current) {
          showToast("Failed to fetch data", "error");
        }
        setData([]);
        setTotalCount(0);
        setTotalPages(0);
        setTabCounts({});
      } finally {
        setLoading(false);
      }
    },
    [filters, generalSearch, pageSize, activeTab, tabs, updateURL, showToast, fetchTabCounts, loading]
  );

  const handleFilterChange = (field, value) => {
    const updates = typeof field === "object" ? field : { [field]: value };
    const newFilters = { ...filters, ...updates };
    setFilters(newFilters);

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (field === "date_from" || field === "date_to") {
      const bothFilled = newFilters.date_from && newFilters.date_to;
      const bothEmpty = !newFilters.date_from && !newFilters.date_to;

      if (bothFilled || bothEmpty) {
        searchTimeoutRef.current = setTimeout(() => {
          fetchData(1, true, null, newFilters, generalSearch);
        }, 600);
      }
    } else {
      searchTimeoutRef.current = setTimeout(() => {
        fetchData(1, true, null, newFilters, generalSearch);
      }, 600);
    }
  };

  const handleGeneralSearch = () => {
    const trimmedSearch = generalSearch.trim();
    fetchData(1, true, null, filters, trimmedSearch);
  };

  const handleGeneralSearchChange = (value) => {
    setGeneralSearch(value);
    if (value.trim() === "") {
      const params = new URLSearchParams(searchParams);
      params.delete("search");
      setSearchParams(params, { replace: true });
      fetchData(1, true, null, filters, "");
    }
  };

  const handleClear = () => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    const clearedFilters = {
      report_type: "",
      sales_area: "",
      entity: "",
      customer_name: "",
      customer_entity: [],
      country_name: "",
      site_name: "",
      date_from: "",
      date_to: "",
      failure_reason: "",
      part_number: "",
      description: "",
      rig_model: "",
      rig_id: "",
    };

    setFilters(clearedFilters);
    setGeneralSearch("");
    setCurrentPage(1);

    const params = new URLSearchParams();
    if (activeTab !== "RTC") params.set("tab", activeTab);
    setSearchParams(params, { replace: true });

    fetchData(1, true, null, clearedFilters, "");
  };

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    setCurrentPage(1);
    fetchData(1, true, tabKey, filters, generalSearch);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchData(newPage);
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
    fetchData(1, true, null, null, null, newPageSize);
  };

  const handleViewDocument = (reportId) => {
    navigate(`/admt/document/${reportId}`);
  };

  const handleDownloadDocument = async (reportId) => {
    try {
      showToast("Preparing download...", "info");
      const blob = await admtApiService.downloadDocument(reportId);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `document_${reportId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showToast("Download started", "success");
    } catch (error) {
      console.error("Download error:", error);
      showToast("Download failed", "error");
    }
  };

  const handleShareDocument = (reportId) => {
    const shareUrl = `${window.location.origin}/admt/document/${reportId}`;
    if (navigator.share) {
      navigator.share({ title: "Sandvik ADMT Document", url: shareUrl });
    } else {
      navigator.clipboard.writeText(shareUrl);
      showToast("Link copied to clipboard", "success");
    }
  };

  const handleBookmarkDocument = async (reportId) => {
    try {
      if (bookmarkedIds.includes(reportId)) {
        await apiService.removeFavorite(reportId);
        setBookmarkedIds((prev) => prev.filter((id) => id !== reportId));
        showToast("Removed from bookmarks", "success");
      } else {
        await apiService.addFavorite(reportId);
        setBookmarkedIds((prev) => [...prev, reportId]);
        showToast("Bookmarked!", "success");
      }
    } catch (error) {
      console.error("Bookmark error:", error);
      showToast("Failed to update bookmark", "error");
    }
  };

  const handleViewPdf = async (reportId) => {
    setPdfReportId(reportId);
    setPdfLoading(true);
    setPdfError(null);
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    try {
      const blobUrl = await admtApiService.getPdfPreviewUrl(reportId);
      setPdfUrl(blobUrl);
    } catch (err) {
      setPdfError(err.message);
    } finally {
      setPdfLoading(false);
    }
  };

  const closePdfViewer = () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfReportId(null);
    setPdfUrl(null);
    setPdfError(null);
  };

  const getActiveFilters = () => {
    return Object.entries(filters).filter(([key, value]) => {
      if (key === "date_from" || key === "date_to") {
        return filters.date_from && filters.date_to && value && value !== "";
      }
      if (Array.isArray(value)) {
        return value.length > 0 && key !== "report_type";
      }
      if (typeof value === "string") {
        const trimmed = value.trim();
        return trimmed && trimmed !== "(Empty/Not Specified)" && key !== "report_type";
      }
      return Boolean(value) && key !== "report_type";
    });
  };

  const removeFilter = (filterKey) => {
    if (filterKey === "date_from" || filterKey === "date_to") {
      const newFilters = { ...filters };
      newFilters.date_from = "";
      newFilters.date_to = "";
      setFilters(newFilters);

      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => {
        fetchData(1, true, null, newFilters, generalSearch);
      }, 300);
    } else if (filterKey === "customer_name" || filterKey === "customer_entity") {
      handleFilterChange({ customer_name: "", customer_entity: [] });
    } else {
      const clearedValue = Array.isArray(filters[filterKey]) ? [] : "";
      handleFilterChange(filterKey, clearedValue);
    }
  };

  useEffect(() => {
    return () => {
      if (countTimeoutRef.current) clearTimeout(countTimeoutRef.current);
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      countCallInProgressRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!showSearchInfo) return;
    const handleClickOutside = (e) => {
      if (searchInfoRef.current && !searchInfoRef.current.contains(e.target)) {
        setShowSearchInfo(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showSearchInfo]);

  const activeFilters = getActiveFilters();

  return (
    <div className="h-[calc(100vh-4rem)] bg-gray-50 flex overflow-hidden">
      {loading && <LoadingScreen />}

      <div
        className={`transition-all duration-300 ease-in-out ${
          sidebarOpen ? "w-80" : "w-0"
        } overflow-hidden flex-shrink-0`}
      >
        <Sidebar
          filters={filters}
          onFilterChange={handleFilterChange}
          onClear={handleClear}
          loading={loading}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          api={admtApiService}
          activeLabel="ADMT reports"
        />
      </div>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <div className="bg-white border-b border-gray-200 px-6 py-2 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <Menu className="h-5 w-5" />
              </button>

              <div className="flex items-center text-sm text-gray-600">
                <button onClick={() => navigate("/")} className="hover:text-black transition-colors">
                  Home
                </button>
                <ChevronRight className="h-4 w-4 mx-2" />
                <span className="text-gray-900">ADMT reports</span>
              </div>
            </div>

            <h1 className="text-xl text-gray-900">ADMT archived reports</h1>
          </div>
        </div>

        <div className="bg-white p-6 border-b border-gray-200 flex-shrink-0">
          {(activeFilters.length > 0 || generalSearch.trim()) && (
            <div className="mb-6 p-4 bg-gray-50 border">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm text-gray-900">
                  Applied filters ({activeFilters.length + (generalSearch.trim() ? 1 : 0)})
                </h3>
                <button
                  onClick={handleClear}
                  className="text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1"
                >
                  <Trash2 className="h-3 w-3" />
                  Clear all
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {generalSearch.trim() && (
                  <span className="inline-flex items-center px-3 py-1 bg-gold-g1 text-gold-g3 border">
                    <span className="mr-1 text-gold-g3">
                      {generalSearch.trim().startsWith("*") ? "Part number:" : "General Search:"}
                    </span>
                    {generalSearch}
                    <button
                      onClick={() => handleGeneralSearchChange("")}
                      className="ml-2 text-gold-g3 hover:text-gold-g3"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                )}
                {activeFilters.map(([key, value]) => {
                  const displayValue = Array.isArray(value) ? value.join(", ") : value;
                  return (
                    <span
                      key={key}
                      className="inline-flex items-center px-3 py-1 bg-gray-200 text-gray-800 border"
                    >
                      <span className="mr-1 text-gray-600">
                        {(() => {
                          const text = key.replace(/_/g, " ");
                          return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
                        })()}
                        :
                      </span>
                      {displayValue.length > 25 ? `${displayValue.substring(0, 25)}...` : displayValue}
                      <button onClick={() => removeFilter(key)} className="ml-2 text-gray-600 hover:text-gray-800">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mb-4">
            <div className="flex items-center space-x-4">
              <div className="flex-1 max-w-md relative">
                <input
                  type="text"
                  placeholder="Search across site names, customer names, failure reasons, serial numbers..."
                  value={generalSearch}
                  onChange={(e) => handleGeneralSearchChange(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleGeneralSearch()}
                  className="w-full border border-gray-300 pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-black focus:border-electric-blue"
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-n5" />
              </div>
              <button
                onClick={handleGeneralSearch}
                disabled={loading}
                className="bg-electric-blue text-white px-4 py-2 hover:bg-electric-blue-eb3 disabled:opacity-50 flex items-center gap-2"
              >
                <Search className="h-4 w-4" />
                Search
              </button>
              <div className="relative" ref={searchInfoRef}>
                <button
                  onClick={() => setShowSearchInfo((v) => !v)}
                  className="p-1.5 text-n5 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
                  title="How does search work?"
                >
                  <Info className="h-4 w-4" />
                </button>
                {showSearchInfo && (
                  <div className="absolute right-0 top-8 z-50 w-72 bg-white border border-gray-200 shadow-lg p-3 text-left">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs text-gray-900">How search works</h4>
                      <button onClick={() => setShowSearchInfo(false)} className="text-n5 hover:text-gray-600">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="space-y-2 text-xs text-gray-700">
                      <div>
                        <p className=" text-gray-800 mb-1">General search</p>
                        <p className="text-gray-500">
                          e.g. <code className="bg-gray-100 px-1 font-mono">Atlas</code> → matches customer names, site names, serial numbers
                        </p>
                      </div>
                      <div>
                        <p className=" text-gray-800 mb-1">Sidebar filter (partial)</p>
                        <p className="text-gray-500">
                          e.g. <code className="bg-gray-100 px-1 font-mono">81</code> → finds any part number containing 81
                        </p>
                      </div>
                      <div className="bg-gold-g1 border border-gold-g2 p-2">
                        <p className=" text-gold-g3 mb-1">
                          Wildcard <code className="bg-gold-g1 px-0.5 font-mono">*</code>
                        </p>
                        <p className="text-gold-g3 mb-1">
                          Prefix with <code className="bg-gold-g1 px-0.5 font-mono">*</code> to match{" "}
                          <span className="">part numbers ending with</span> a value:
                        </p>
                        <p className="text-gold-g3">
                          <code className="bg-gold-g1 px-1 font-mono">*81</code> → part numbers ending in 81
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mb-0">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => handleTabChange(tab.key)}
                    className={`py-2 px-1 border-b-2 text-sm flex items-center gap-2 ${
                      activeTab === tab.key
                        ? "border-electric-blue text-black"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    {tab.label}
                    {tabCounts[tab.key] !== undefined && (
                      <span
                        className={`px-2 py-0.5 ${
                          activeTab === tab.key ? "bg-electric-blue text-white" : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {tabCounts[tab.key]}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col min-h-0" style={{ paddingLeft: "1rem", paddingRight: "1rem" }}>
          <div className="flex-1 overflow-auto">
            <TableComponent
              data={data}
              loading={loading}
              filters={filters}
              activeTab={activeTab}
              onViewDocument={handleViewDocument}
              onDownloadDocument={handleDownloadDocument}
              onShareDocument={handleShareDocument}
              onBookmarkDocument={handleBookmarkDocument}
              onViewPdf={handleViewPdf}
              bookmarkedIds={bookmarkedIds}
              getTableHeaders={getTableHeaders}
              getConfigurationForReportType={getConfigurationForReportType}
            />
          </div>

          <div className="bg-white flex-shrink-0">
            <Pagination
              currentPage={currentPage}
              pageSize={pageSize}
              totalCount={totalCount}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
        </div>
      </div>

      {pdfReportId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={closePdfViewer}>
          <div className="bg-white w-[90vw] h-[90vh] shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center space-x-3">
                <FileText className="h-5 w-5 text-gray-700" />
                <span className=" text-gray-800">Report PDF — {pdfReportId}</span>
              </div>
              <div className="flex items-center space-x-2">
                {pdfUrl && (
                  <a
                    href={pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1.5 text-xs text-gray-600 hover:text-black border border-gray-300 hover:border-gray-400 transition-colors flex items-center space-x-1"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    <span>Open in tab</span>
                  </a>
                )}
                <button onClick={closePdfViewer} className="p-1.5 hover:bg-gray-200 transition-colors">
                  <X className="h-5 w-5 text-gray-600" />
                </button>
              </div>
            </div>
            <div className="flex-1 relative">
              {pdfLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
                  <RefreshCw className="h-8 w-8 text-n5 animate-spin" />
                </div>
              )}
              {pdfError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50">
                  <AlertCircle className="h-10 w-10 text-red-400 mb-3" />
                  <p className="text-sm text-gray-600">{pdfError}</p>
                </div>
              )}
              {pdfUrl && !pdfLoading && (
                <object data={pdfUrl} type="application/pdf" className="w-full h-full">
                  <embed src={pdfUrl} type="application/pdf" className="w-full h-full" />
                </object>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdmtReportsPage;
